import httpUtil from '@/utils/http';
import { createProjectSSEClient } from '@/utils/sse';

const request = httpUtil.mfd.request;
const prefix = '/v1';

export interface AIAnalysisRequestParams {
  project_id: number;
  gitlab_project_id: string;
  from_ref: string;
  to_ref: string;
  plan_id?: number;
  plan_name?: string;
  project_name?: string;
  version?: string;
}

export interface SSEMessageData {
  type: 'progress' | 'ai_thinking' | 'ai_content' | 'debug_info' | 'result' | 'error';
  content: any;
}

export interface AIAnalysisSSEHandler {
  onMessage?: (data: SSEMessageData) => void;
  onError?: (error: Error) => void;
  onComplete?: () => void;
  onOpen?: () => void;
}

export const analysisApi = {
  /**
   * @description AI智能分析（SSE流式传输）
   * POST /v1/precision-testing/ai-analysis/streaming
   * @param params 分析参数
   * @param handlers SSE事件处理器
   * @param options 配置选项
   * @returns 返回一个控制对象，包含取消方法
   */
  startAIAnalysisStreaming(
    params: AIAnalysisRequestParams,
    handlers: AIAnalysisSSEHandler = {},
    options: { timeout?: number } = {},
  ): { cancel: () => void; isConnected: () => boolean } {
    const sseClient = createProjectSSEClient();
    const baseURL = import.meta.env.VITE_MFD_BASE_URL || '';
    const url = `${baseURL}${prefix}/precision-testing/ai-analysis/streaming`;

    // 开始SSE连接
    sseClient.connect(
      url,
      params,
      {
        onMessage: handlers.onMessage,
        onError: handlers.onError,
        onComplete: handlers.onComplete,
        onOpen: handlers.onOpen,
      },
      {
        timeout: options.timeout || 0, // 默认无超时限制
      },
    );

    return {
      cancel: () => sseClient.abort(),
      isConnected: () => sseClient.isConnected(),
    };
  },

  /**
   * @description AI智能分析（传统请求方式，用于不支持SSE的场景）
   * POST /v1/precision-testing/ai-analysis/start
   */
  startAIAnalysis(params: AIAnalysisRequestParams) {
    return request({
      url: `${prefix}/precision-testing/ai-analysis/start`,
      method: 'POST',
      data: params,
    });
  },

  /**
   * @description 获取AI分析结果
   * GET /v1/precision-testing/ai-analysis/result/:jobId
   */
  getAIAnalysisResult(jobId: string) {
    return request({
      url: `${prefix}/precision-testing/ai-analysis/result/${jobId}`,
      method: 'GET',
    });
  },

  /**
   * @description 取消AI分析任务
   * POST /v1/precision-testing/ai-analysis/cancel/:jobId
   */
  cancelAIAnalysis(jobId: string) {
    return request({
      url: `${prefix}/precision-testing/ai-analysis/cancel/${jobId}`,
      method: 'POST',
    });
  },
};
